/**
 * Capital projects: the portfolio, its stage gates and its hold points.
 *
 * Shapes follow `projects.py` and the services behind it (`portfolio.py`,
 * `gates.py`, `inspections.py`, `attention.py`) and migration 0019. Three
 * things the routers settled that shape everything here:
 *
 * **Deciding a gate is not writing a project**, and **releasing a hold point
 * is not recording a result.** The platform gives each of those its own
 * permission (`project:gate`, `project:release_hold`) precisely so the two
 * acts cannot be collapsed. This file keeps them apart too: the screen offers
 * them separately and disables each on its own key.
 *
 * **The platform reports states, not numbers it cannot justify.** A schedule
 * is `no_baseline` rather than a slip of zero; money is `not_declared` rather
 * than 0; a sheet is `incomplete` rather than passed. Every one of those has
 * a `why` in words, and this app shows it rather than re-deriving it — the
 * platform has the final word, and a second opinion computed here would be a
 * different answer wearing the same label.
 */
import { locale, t } from "../i18n";

/* ---- the portfolio -------------------------------------------------------------- */

export const PROJECT_STATUSES = [
  "planned",
  "design",
  "tender",
  "construction",
  "commissioning",
  "complete",
  "on_hold",
  "cancelled",
] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const PROJECT_STATUS_LABEL: Record<ProjectStatus, string> = {
  planned: "Planned",
  design: "Design",
  tender: "Tender",
  construction: "Construction",
  commissioning: "Commissioning",
  complete: "Complete",
  on_hold: "On hold",
  cancelled: "Canceled",
};

/**
 * `LIVE_STATUSES` in `portfolio.py`, and it deliberately excludes `on_hold`
 * even though a held project is not finished. The platform's `live_only`
 * filter and every "needs attention" query use this list, so the screen's
 * "Live only" filter must mean the same thing or the counts beside it will
 * not add up to the rows under it.
 */
export const LIVE_STATUSES: readonly ProjectStatus[] = ["planned", "design", "tender", "construction", "commissioning"];
export const CLOSED_STATUSES: readonly ProjectStatus[] = ["complete", "cancelled"];

/** `schedule_of` in portfolio.py. Not a number, because for three of these there is no number. */
export type ScheduleState = "on_baseline" | "ahead" | "slipped" | "no_baseline" | "no_current_date" | "no_dates";

export const SCHEDULE_LABEL: Record<ScheduleState, string> = {
  on_baseline: "On baseline",
  ahead: "Ahead of baseline",
  slipped: "Slipped",
  no_baseline: "No baseline",
  no_current_date: "No current date",
  no_dates: "No dates",
};

export interface Schedule {
  state: ScheduleState;
  /** The platform's own sentence. Shown verbatim; never re-worded. */
  why?: string;
  baselineStart?: string;
  baselineEnd?: string;
  startDate?: string;
  endDate?: string;
  /** null when the state is one of the three that cannot answer it. */
  endSlipDays?: number;
  startSlipDays?: number;
  /** Distinct from slip: a project on its baseline can still have run past it. */
  overdueDays?: number;
  /** How many times the baseline has been moved. Slip is measured against the current one. */
  rebaselined: number;
  rebaselineNote?: string;
}

/**
 * `money_of` in portfolio.py, and note what is absent: no variance, no
 * remaining, no percentage. The platform is not told whether `committed`
 * includes `spent`, so it computes none of them and neither does this app.
 * Three figures as they were given, or `not_declared`.
 */
export interface Money {
  currency?: string;
  budgetMinor?: number;
  committedMinor?: number;
  spentMinor?: number;
  state: "declared" | "not_declared";
  note?: string;
}

export interface ProjectRow {
  id: string;
  code: string;
  name: string;
  category?: string;
  domain?: string;
  status: ProjectStatus;
  siteId?: string;
  siteCode?: string;
  ownerId?: string;
  contractor?: string;
  schedule: Schedule;
  money: Money;
  gateSet?: { key: string; version?: string; pack?: string };
  gates: number;
  gatesApproved: number;
  /** Unreleased hold points on this project. Any at all means the work is stopped. */
  holding: number;
  workStopped: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface Rebaseline {
  baselineStart?: string;
  baselineEnd?: string;
  replacedAt?: string;
  replacedBy?: string;
  reason?: string;
}

export interface ProjectDetail extends ProjectRow {
  description?: string;
  consultant?: string;
  ownerName?: string;
  gateList: Gate[];
  gateSummary: GateSummary;
  inspections: ProjectInspectionRow[];
  links: ProjectLink[];
  holdPoints: HoldPoint[];
  rebaselines: Rebaseline[];
}

export interface ProjectLink {
  entityKind: string;
  entityId: string;
  relation?: string;
  /** Resolved by the platform per kind — an asset tag, a work-order number, a document reference. */
  entityRef?: string;
  entityName?: string;
}

/* ---- gates ---------------------------------------------------------------------- */

export const GATE_STATUSES = ["pending", "submitted", "approved", "rejected", "waived"] as const;
export type GateStatus = (typeof GATE_STATUSES)[number];

export const GATE_STATUS_LABEL: Record<GateStatus, string> = {
  pending: "Not submitted",
  submitted: "Awaiting decision",
  approved: "Approved",
  rejected: "Rejected",
  waived: "Waived",
};

/** `DECIDED` in gates.py — a gate in one of these has been decided, whichever way. */
export const GATE_DECIDED: readonly GateStatus[] = ["approved", "rejected", "waived"];

/** `DECISIONS` in gates.py: the three a person may record. */
export const GATE_DECISIONS = ["approved", "rejected", "waived"] as const;
export type GateDecision = (typeof GATE_DECISIONS)[number];

export const GATE_DECISION_VERB: Record<GateDecision, string> = {
  approved: "Approve",
  rejected: "Reject",
  waived: "Waive",
};

/** `evidence_state` in gates.py. Five states, and none can be mistaken for another. */
export type EvidenceState = "none_required" | "complete" | "incomplete" | "decided_without_evidence" | "decided";

export interface Evidence {
  state: EvidenceState;
  required: string[];
  supplied: string[];
  missing: string[];
  why?: string;
}

export interface Gate {
  id: string;
  seq: number;
  key: string;
  name: string;
  status: GateStatus;
  evidence: Evidence;
  dueDate?: string;
  submittedAt?: string;
  decidedAt?: string;
  decidedByName?: string;
  decisionNote?: string;
}

export interface GateSummary {
  count: number;
  decided: number;
  /** The first gate nobody has decided. Null — not absent — when they all are. */
  nextGate: string | null;
  decidedWithoutEvidence: string[];
  /** `no_gates` is not `all_decided`: a project with no gates has passed none. */
  state: "no_gates" | "in_progress" | "all_decided";
  why?: string;
}

/**
 * What the screen offers on a gate.
 *
 * Submitting is refused by `gates.submit` once a gate is decided — "a decided
 * gate is re-opened by deciding it again, not by resubmitting it" — so the
 * button is withheld there rather than offered and refused. Deciding stays
 * offered on a decided gate, because deciding again is how the platform says
 * a gate is re-opened.
 */
export function canSubmitGate(gate: Gate): boolean {
  return !GATE_DECIDED.includes(gate.status);
}

/**
 * A waiver needs a reason and `gates.decide` refuses one without it: "a waiver
 * with no reason reads afterwards as a gate that never applied". The dialog
 * requires the note before enabling the button, so the refusal is prevented
 * rather than displayed — the platform still enforces it.
 */
export function decisionRequiresNote(decision: GateDecision): boolean {
  return decision === "waived";
}

/* ---- inspections, hold points and the sheet -------------------------------------- */

export const INSPECTION_KINDS = ["hold", "witness", "surveillance", "re_inspection", "final", "handover"] as const;
export type InspectionKind = (typeof INSPECTION_KINDS)[number];

export const INSPECTION_KIND_LABEL: Record<InspectionKind, string> = {
  hold: "Hold point",
  witness: "Witness point",
  surveillance: "Surveillance",
  re_inspection: "Re-inspection",
  final: "Final",
  handover: "Handover",
};

export const INSPECTION_RESULTS = ["pass", "pass_with_observations", "conditional", "fail", "not_applicable"] as const;
export type InspectionResult = (typeof INSPECTION_RESULTS)[number];

export const INSPECTION_RESULT_LABEL: Record<InspectionResult, string> = {
  pass: "Pass",
  pass_with_observations: "Pass with observations",
  conditional: "Conditional",
  fail: "Fail",
  not_applicable: "Not applicable",
};

/** `ASSERTS_ACCEPTABLE` in inspections.py: recording one of these over an unchecked item is refused. */
export const ASSERTS_ACCEPTABLE: readonly InspectionResult[] = ["pass", "pass_with_observations"];

/** `BLOCKS_RELEASE`: a failed hold point is cleared by re-inspecting, not by signing over the failure. */
export const BLOCKS_RELEASE: readonly InspectionResult[] = ["fail"];

export const ITEM_RESULTS = ["pass", "fail", "not_applicable", "not_checked"] as const;
export type ItemResult = (typeof ITEM_RESULTS)[number];

export const ITEM_RESULT_LABEL: Record<ItemResult, string> = {
  pass: "Pass",
  fail: "Fail",
  not_applicable: "N/A",
  not_checked: "Not checked",
};

/** `quality_of` in inspections.py. `complete` says every item was answered — not that they passed. */
export type QualityState = "no_items" | "not_started" | "incomplete" | "complete";

export interface Quality {
  state: QualityState;
  items: number;
  passed: number;
  failed: number;
  notApplicable: number;
  notChecked: number;
  why?: string;
}

export interface InspectionItem {
  seq: number;
  itemKey?: string;
  prompt?: string;
  result: ItemResult;
  valueNum?: number;
  valueText?: string;
  tolerance?: string;
  note?: string;
  /** Set once a failure has been raised onto the platform's own defect register. */
  defectId?: string;
  defectStatus?: string;
  defectSeverity?: number;
}

export interface ProjectInspectionRow {
  id: string;
  reference: string;
  title: string;
  kind: InspectionKind;
  discipline: string;
  checklistKey?: string;
  location?: string;
  scheduledFor?: string;
  performedAt?: string;
  performedByName?: string;
  result?: InspectionResult;
  releasedAt?: string;
  releasedByName?: string;
  witnessedBy?: string;
  items: number;
  notChecked: number;
  failed: number;
  defects: number;
  /** A hold point that has not been released. The work it covers is stopped. */
  stoppingWork: boolean;
  /** The platform's sentence saying why it is still holding. */
  holdingBecause?: string;
  createdAt: string;
}

export interface ProjectInspectionDetail extends Omit<ProjectInspectionRow, "items" | "notChecked" | "failed"> {
  projectId: string;
  projectCode: string;
  projectName?: string;
  siteId?: string;
  notes?: string;
  sheet: InspectionItem[];
  quality: Quality;
}

/** A row of `GET /v1/projects/hold-points` — every unreleased hold point in the portfolio. */
export interface HoldPoint {
  id: string;
  reference: string;
  title: string;
  discipline: string;
  location?: string;
  kind: InspectionKind;
  result?: InspectionResult;
  scheduledFor?: string;
  performedAt?: string;
  projectId: string;
  projectCode: string;
  projectName?: string;
  projectStatus?: ProjectStatus;
  siteId?: string;
  siteCode?: string;
  items: number;
  notChecked: number;
  failed: number;
  holdingBecause: string;
  /** Inspected, passed, sheet finished: the only thing left is the signature. */
  awaitingReleaseOnly: boolean;
  createdAt: string;
}

export interface HoldPointReport {
  holdPoints: HoldPoint[];
  count: number;
  holdPointsEver: number;
  /** `nothing_holding` is a statement about hold points only, not a clearance to proceed. */
  state: "work_stopped" | "nothing_holding";
  note?: string;
}

/**
 * Why the platform would refuse to release this hold point, in its own terms.
 *
 * `inspections.release` names four refusals and this mirrors three of them —
 * the fourth (a credential tied to nobody) is not knowable here. The screen
 * uses this to explain a disabled Release button rather than to enforce
 * anything: the platform decides, and its refusal is shown verbatim if one
 * comes back anyway.
 *
 * Note what is NOT a blocker. A result of `conditional` or `not_applicable`
 * does not block: the platform blocks only `fail`, on the reasoning that a
 * failed hold point is cleared by re-inspecting the work. Second-guessing
 * that here would disable a button the platform would have honoured.
 */
export function releaseBlockers(i: {
  kind: InspectionKind;
  result?: InspectionResult;
  releasedAt?: string;
  quality: Pick<Quality, "notChecked" | "items">;
}): string[] {
  const blockers: string[] = [];
  if (i.kind !== "hold") {
    blockers.push(
      t("This is a {kind}, which does not stop the work and so has nothing to release.", {
        kind: t(INSPECTION_KIND_LABEL[i.kind]).toLowerCase(),
      }),
    );
    return blockers;
  }
  if (i.releasedAt) blockers.push(t("This hold point has already been released."));
  if (!i.result) {
    blockers.push(
      t(
        "No result has been recorded, so nothing has been inspected. Releasing it would be a signature against a check that never happened.",
      ),
    );
  } else if (BLOCKS_RELEASE.includes(i.result)) {
    blockers.push(
      t(
        "The inspection failed. A failed hold point is cleared by re-inspecting the work — raise a re-inspection and release that.",
      ),
    );
  }
  if (i.quality.notChecked > 0) {
    blockers.push(
      t(
        "{n} of {total} items are unchecked. Releasing past an unfinished sheet is what the hold point was placed to prevent.",
        {
          n: i.quality.notChecked,
          total: i.quality.items,
        },
      ),
    );
  }
  return blockers;
}

/**
 * Whether recording this result would be refused for an unfinished sheet.
 *
 * `inspections.record_result` refuses a pass over unchecked items, because an
 * unchecked item is not a pass. Offering the result and letting the platform
 * refuse would be honest but slower; withholding it silently would hide the
 * rule. So the screen offers it and says why it is unavailable.
 */
export function resultBlockedByUncheckedItems(result: InspectionResult, quality: Pick<Quality, "notChecked">): boolean {
  return ASSERTS_ACCEPTABLE.includes(result) && quality.notChecked > 0;
}

/* ---- what nobody is looking at --------------------------------------------------- */

/**
 * `attention.report`. Each list is a different failure with a different fix,
 * and the platform's own note insists they not be added together:
 * `no_baseline` is not an on-time project, `never_inspected` is not a project
 * with nothing wrong. They are rendered as separate lists for that reason.
 */
export const ATTENTION_KINDS = [
  "work_stopped",
  "gates_awaiting_decision",
  "gates_decided_without_evidence",
  "sheets_left_incomplete",
  "overdue",
  "slipped_against_baseline",
  "no_baseline",
  "never_inspected",
  "rebaselined",
] as const;
export type AttentionKind = (typeof ATTENTION_KINDS)[number];

export const ATTENTION_LABEL: Record<AttentionKind, string> = {
  work_stopped: "Work stopped",
  gates_awaiting_decision: "Gates awaiting decision",
  gates_decided_without_evidence: "Gates decided without evidence",
  sheets_left_incomplete: "Sheets left incomplete",
  overdue: "Overdue",
  slipped_against_baseline: "Slipped against baseline",
  no_baseline: "No baseline",
  never_inspected: "Never inspected",
  rebaselined: "Rebaselined",
};

/** One line each, saying what the list means and why it is not one of the others. */
export const ATTENTION_MEANING: Record<AttentionKind, string> = {
  work_stopped: "Unreleased hold points. Each stops the work it covers until somebody releases it.",
  gates_awaiting_decision: "Submitted and waiting on a decision.",
  gates_decided_without_evidence:
    "Decided anyway, with required items never supplied. Recorded for the project's life.",
  sheets_left_incomplete:
    "A result was recorded while items were still unchecked — work somebody believes is finished.",
  overdue: "Live and past the current end date.",
  slipped_against_baseline: "The current end date is later than the baseline.",
  no_baseline: "Nothing can be late against no baseline, so these never appear above however far they run.",
  never_inspected: "In construction or commissioning with no inspection recorded at all.",
  rebaselined: "The baseline has been moved. Slip is measured against the current one.",
};

export interface AttentionRow {
  projectId: string;
  projectCode?: string;
  projectName?: string;
  name?: string;
  status?: ProjectStatus;
  /* gate rows */
  key?: string;
  seq?: number;
  submittedAt?: string;
  dueDate?: string;
  daysWaiting?: number;
  overdue?: boolean;
  /* inspection rows */
  inspectionId?: string;
  reference?: string;
  title?: string;
  kind?: InspectionKind;
  discipline?: string;
  result?: InspectionResult;
  performedAt?: string;
  notChecked?: number;
  items?: number;
  /* schedule rows */
  endDate?: string;
  baselineEnd?: string;
  startDate?: string;
  daysOverdue?: number;
  endSlipDays?: number;
  rebaselined?: number;
  /* hold-point rows carry the full shape; the fields above cover what is shown */
  holdingBecause?: string;
}

export interface AttentionReport {
  asOf?: string;
  siteId?: string;
  lists: Record<AttentionKind, AttentionRow[]>;
  counts: Record<AttentionKind, number>;
  note?: string;
}

/* ---- the packs' vocabulary ------------------------------------------------------- */

/**
 * The gate sets, disciplines and categories the installed packs declare.
 * Published by the platform for the reason the safety vocabulary is: a client
 * that hard-codes a gate model is wrong at the first operator who runs a
 * different one. So nothing here is hard-coded — the screen labels a gate with
 * what the pack calls it and offers no gate model of its own.
 */
export interface GateSetTerm {
  key: string;
  version?: string;
  name: string;
  pack?: string;
  description?: string;
  gates: { key: string; name: string; requires: string[]; description?: string }[];
}

export interface VocabularyTerm {
  key: string;
  name: string;
  pack?: string;
  domain?: string;
  description?: string;
  defaultGateSet?: string;
}

export interface ProjectVocabulary {
  gateSets: GateSetTerm[];
  disciplines: VocabularyTerm[];
  categories: VocabularyTerm[];
  /**
   * Why the vocabulary is thin, when it is. An empty vocabulary because no
   * pack declares one and an empty one because the files are missing from
   * this node are different faults with different fixes.
   */
  issues: string[];
}

/* ---- reading the portfolio ------------------------------------------------------- */

/**
 * Portfolio order: what is stopping work, then what is late, then the rest.
 *
 * Deliberately not sorted by budget or by size. The question this screen
 * answers is "what needs somebody today", and a stopped £2m project and a
 * stopped £20k one both stop the same way.
 */
export function sortProjects(rows: readonly ProjectRow[]): ProjectRow[] {
  const rank = (p: ProjectRow): number => {
    if (p.workStopped) return 0;
    if (p.schedule.overdueDays != null && p.schedule.overdueDays > 0) return 1;
    if (p.schedule.state === "slipped") return 2;
    if (p.schedule.state === "no_baseline") return 3;
    return 4;
  };
  return [...rows].sort((a, b) => rank(a) - rank(b) || a.code.localeCompare(b.code));
}

/** Live is the platform's list, so the filter and the counts beside it agree. */
export function isLive(status: ProjectStatus): boolean {
  return LIVE_STATUSES.includes(status);
}

/**
 * Money for display.
 *
 * The platform stores whole minor units and refuses to divide, because it is
 * not told what a currency's minor unit divides by. `Intl.NumberFormat` is
 * told: it carries the ISO 4217 exponent for every code, so formatting
 * through it uses the published exponent rather than an assumption that every
 * currency has two decimal places — which is wrong for JOD (three) and for
 * JPY (none).
 *
 * What is still not computed here is any figure the platform declined to
 * compute: no variance, no remaining, no percentage of budget. The three
 * declared numbers, and nothing derived from them.
 */
export function formatMoney(minor: number | undefined, currency: string | undefined): string {
  if (minor == null) return "—";
  const code = currency || "SAR";
  try {
    const fmt = new Intl.NumberFormat(locale(), { style: "currency", currency: code, maximumFractionDigits: 0 });
    const exponent = new Intl.NumberFormat(undefined, { style: "currency", currency: code }).resolvedOptions()
      .maximumFractionDigits;
    return fmt.format(minor / 10 ** (exponent ?? 2));
  } catch {
    /* An unknown code: report the number and the code rather than guess an exponent. */
    return t("{amount} {currency} (minor units)", { amount: minor.toLocaleString(locale()), currency: code });
  }
}

/* ---- what the screen sends ------------------------------------------------------- */

export interface GateSubmitInput {
  /** Evidence keys; the platform accepts strings or objects and accumulates rather than replacing. */
  evidence?: string[];
  dueDate?: string;
}

export interface GateDecisionInput {
  decision: GateDecision;
  note?: string;
  evidence?: string[];
}

export interface GateDecisionResult {
  key: string;
  decision: GateDecision;
  decidedAt?: string;
  projectCode?: string;
  evidence: Evidence;
  /** Earlier gates nobody has decided. Reported by the platform, never refused. */
  outOfOrderAfter: string[];
  /** The platform's own warnings — a gate decided without evidence says so here. */
  warnings: string[];
}

export interface GateSubmitResult {
  key: string;
  status: GateStatus;
  submittedAt?: string;
  evidence: Evidence;
  warnings: string[];
}

export interface ItemResultInput {
  seq?: number;
  itemKey?: string;
  result: ItemResult;
  valueNum?: number;
  valueText?: string;
  tolerance?: string;
  note?: string;
}

export interface RecordResultInput {
  result: InspectionResult;
  notes?: string;
  witnessedBy?: string;
  performedAt?: string;
}

export interface InspectionWriteResult {
  reference: string;
  result?: InspectionResult;
  quality: Quality;
  performedAt?: string;
  releasedAt?: string;
  /** How many item answers were taken (`record_items`). */
  recorded?: number;
  /**
   * After recording a result on a hold point: it is still holding. The
   * platform says this in its own words because the mistake it prevents is
   * exactly the one worth preventing — a result is not a release, and the
   * work does not restart until somebody with the authority releases it.
   */
  stillStoppingWork?: boolean;
  why?: string;
  warnings: string[];
  note?: string;
}

export interface ProjectQuery {
  siteId: string;
  status?: ProjectStatus;
  category?: string;
  liveOnly?: boolean;
}

/* ---- entering a project ------------------------------------------------------------ */

/** Mirrors the platform's `ProjectIn`. Money in whole minor units, as the platform stores it. */
export interface CreateProjectInput {
  name: string;
  category: string;
  gateSet?: string;
  code?: string;
  description?: string;
  status?: ProjectStatus;
  startDate?: string;
  endDate?: string;
  baselineStart?: string;
  baselineEnd?: string;
  budgetMinor?: number;
  currency?: string;
  contractor?: string;
  consultant?: string;
}

/** Mirrors `UpdateIn`: descriptive and financial fields only — dates go through the schedule. */
export interface UpdateProjectInput {
  name?: string;
  description?: string;
  status?: ProjectStatus;
  category?: string;
  budgetMinor?: number;
  committedMinor?: number;
  spentMinor?: number;
  contractor?: string;
  consultant?: string;
}

/** Mirrors `ScheduleIn`. Moving a baseline that exists needs `rebaselineReason`; the platform refuses otherwise. */
export interface ScheduleInput {
  startDate?: string;
  endDate?: string;
  baselineStart?: string;
  baselineEnd?: string;
  rebaselineReason?: string;
}

export interface ScheduleResult {
  rebaselined: boolean;
  warnings: string[];
}

/** A currency's minor-unit exponent from ISO 4217 via Intl, never an assumed two. */
export function currencyExponent(currency: string | undefined): number {
  try {
    return (
      new Intl.NumberFormat("en", { style: "currency", currency: currency || "SAR" }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

/** "1,250,000.50" in the currency → whole minor units; undefined for blank, NaN for nonsense. */
export function toMinor(major: string, currency: string | undefined): number | undefined {
  const text = major.replace(/[,\s]/g, "");
  if (!text) return undefined;
  const n = Number(text);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 10 ** currencyExponent(currency)) : NaN;
}

/** Whole minor units → the plain figure an input holds. */
export function fromMinor(minor: number | undefined, currency: string | undefined): string {
  return minor == null ? "" : String(minor / 10 ** currencyExponent(currency));
}

/* ---- the project log ------------------------------------------------------------------ */

export const LOG_KINDS = ["note", "update", "action"] as const;
export type LogKind = (typeof LOG_KINDS)[number];

export const LOG_KIND_LABEL: Record<LogKind, string> = {
  note: "Note",
  update: "Progress update",
  action: "Action",
};

export type ActionStatus = "open" | "done" | "cancelled";

export interface ProjectLogEntry {
  id: string;
  kind: LogKind;
  body: string;
  /** What somebody reported on an update — never derived from dates or money. */
  progressPct?: number;
  owner?: string;
  dueDate?: string;
  status?: ActionStatus;
  overdue: boolean;
  closedAt?: string;
  closedBy?: string;
  closureNote?: string;
  createdAt: string;
  createdBy?: string;
}

export interface ProjectLog {
  entries: ProjectLogEntry[];
  openActions: number;
  overdueActions: number;
  latestProgress?: { pct: number; reportedAt: string; reportedBy?: string };
}

export interface NewLogEntryInput {
  kind: LogKind;
  body: string;
  progressPct?: number;
  owner?: string;
  dueDate?: string;
}

export interface CloseActionInput {
  outcome: "done" | "cancelled";
  /** Required to cancel. */
  note?: string;
}
